import { FlexBox } from '@dixa/lib-shared-habla';
import { produce } from 'immer';
import type { Interval } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import moment from 'moment-timezone';
import { useCallback } from 'react';

import { useMigrateOpeningHoursContext } from 'components/powerFlowShared/components/modals/migrateOpeningHours/migrateOpeningHours.context';
import { IntervalsSelectProps } from 'components/settings/components/sections/openingHours/views/schedule/components/scheduleItem/components/intervalsSelect/intervalsSelect';
import { OpeningHours } from 'components/settings/components/sections/openingHours/views/schedule/components/scheduleItem/components/listItem/components/openingHours/openingHours';
import { ListItemContext } from 'components/settings/components/sections/openingHours/views/schedule/components/scheduleItem/components/listItem/listItem.context';
import { validateIntervals } from 'components/settings/components/sections/openingHours/views/schedule/utils/validate';

import type { BusinessHourSchedule } from '../../../../types/businessHourSchedule';

import styles from './edit.module.scss';

type Props = {
  schedule: BusinessHourSchedule[];
};

const weekdays = moment.weekdays();

export function Edit({ schedule }: Props): JSX.Element {
  const {
    intervalsErrors,
    setters: { setIntervalsErrors, setSchedule },
  } = useMigrateOpeningHoursContext();

  const handleValidateInterval = useCallback(
    (dayIndex: number): IntervalsSelectProps['validate'] =>
      (intervals) =>
        void setIntervalsErrors(
          validateIntervals({
            intervals,
            dayIndex,
            errors: intervalsErrors,
          }),
        ),
    [intervalsErrors, setIntervalsErrors],
  );

  const handleOpeningHourChange = useCallback(
    (id: string) => (intervals: readonly Interval[]) =>
      setSchedule(
        produce((schedule) => {
          const index = schedule.findIndex(
            ({ date: scheduleDate }) => scheduleDate === id,
          );
          schedule[index].intervals = [...intervals];
        }),
      ),
    [setSchedule],
  );

  return (
    <ListItemContext.Provider
      value={{
        intervalsSelectProps: {
          errors: intervalsErrors,
          onChange: handleOpeningHourChange,
          validate: handleValidateInterval,
        },
        onAudioMessageChange: (_) => noop,
        onAutoReplyChange: (_) => noop,
        onAutoReplyClear: (_) => noop,
        onDelete: (_) => noop,
        onOverriddenChange: noop,
      }}
    >
      <FlexBox flexDirection="column" gap="250">
        {schedule.map((schedule, index) => (
          <OpeningHours
            key={schedule.date}
            className={styles.openingHours}
            index={index}
            openingHours={{
              id: schedule.date,
              created_at: '',
              created_by: '',
              duration: 1,
              intervals: schedule.intervals,
              name: weekdays[new Date(schedule.date).getDay()],
              opening_hour_type: 'regular',
              recurring: null,
              start_date: schedule.date,
            }}
            itemClassName={styles.openingHoursItem}
          />
        ))}
      </FlexBox>
    </ListItemContext.Provider>
  );
}
